export type EnumDefinition<K extends string, V extends number | string> = {
  readonly [F in K]: V;
};

const valuesCache = new WeakMap<
  EnumDefinition<string, number | string>,
  ReadonlySet<number | string>
>();

const mapsCache = new WeakMap<
  EnumDefinition<string, number | string>,
  ReadonlyMap<string, number | string>
>();

export function extractEnumValues<K extends string, V extends number | string>(
  definition: EnumDefinition<K, V>,
): ReadonlySet<V> {
  let result = valuesCache.get(definition) as ReadonlySet<V> | undefined;

  if (result === undefined) {
    result = new Set<V>(extractEnumEntries(definition).values());
    valuesCache.set(definition, result);
  }

  return result;
}

export function extractEnumEntries<K extends string, V extends number | string>(
  definition: EnumDefinition<K, V>,
): ReadonlyMap<K, V> {
  let result = mapsCache.get(definition) as Map<string, V> | undefined;

  if (result === undefined) {
    result = new Map<string, V>(Object.entries<V>(definition));

    for (const key in definition) {
      if (typeof definition[key] === 'number') {
        result.delete(String(definition[key]));
      }
    }

    mapsCache.set(definition, result);
  }

  return result as unknown as ReadonlyMap<K, V>;
}

export function enumToKey<K extends string, V extends number | string>(
  definition: EnumDefinition<K, V>,
  value: V,
): K {
  for (const [key, item] of extractEnumEntries(definition)) {
    if (item === value) {
      return key;
    }
  }

  throw new Error('Key not found');
}

export interface StatusInfo {
  label: string;
  className: string | null;
}

export type StatusInfoEntry<T extends number | string> = readonly [
  item: T,
  className: string | null,
  label?: string,
];

export function generateStatusInfoMap<
  K extends string,
  V extends number | string,
>(
  enumDef: EnumDefinition<K, V>,
  dictionary: readonly StatusInfoEntry<NoInfer<V>>[],
): Map<V, StatusInfo> {
  const result = new Map<V, StatusInfo>();

  for (const [item, className, label] of dictionary) {
    result.set(item, { label: label ?? enumToKey(enumDef, item), className });
  }

  return result;
}

export function generateStatusInfoEntries<
  K extends string,
  V extends number | string,
>(enumDef: EnumDefinition<K, V>): StatusInfoEntry<V>[] {
  return Array.from(extractEnumValues(enumDef), (item) => [item, null]);
}
